import React, { useEffect, useState } from 'react';
import Icon from '../../../src/common/components/Icon';
import SubscribeButton from '../../below-article/SubButton';
import Toggle from 'react-toggle';
import { useTranslations } from '../../localization/useTranslations';

type AuthorAndTopicCardProps = {
  title: string;
  id: string;
  label?: string;
  type: 'author' | 'topic';
  isFollowed: boolean;
  imageUrl?: string;
  name?: string;
  link?: string;
  description?: string;
  searchModalOpen?: boolean;
  shortDescription?: string;
  onSubscribeClick?: (id: string, type: string) => void;
  onFollowClick: (id: number, type: 'author' | 'topic') => void;
  layout?:
    | 'topic-card'
    | 'in-modal'
    | 'article-top'
    | 'article-bottom'
    | 'followed-authors'
    | 'article-term'
    | 'full-width'
    | 'carousel';
  isFollowable?: boolean;
  hasSocialLinks?: boolean;
  email?: string;
  twitter?: string;
  instagram?: string;
  linkedin?: string;
  nameType?: 'button' | 'link' | 'div';
  openAuthorModal?: () => void;
  handleNewsletterToggle?: (id: string, type: string) => void;
};

const AuthorAndTopicCard: React.FC<AuthorAndTopicCardProps> = ({
  title,
  id,
  label,
  type,
  isFollowed,
  imageUrl,
  name,
  link,
  description,
  shortDescription,
  searchModalOpen,
  onFollowClick,
  layout,
  isFollowable,
  hasSocialLinks,
  email,
  twitter,
  instagram,
  linkedin,
  nameType,
  openAuthorModal,
}) => {
  const [topicVector, setTopicVector] = useState<string>('');
  const translations = useTranslations();

  const handleToggleChange = () => {
    if (!isFollowed) {
      // Only try to subscribe if the toggle is currently off
      onFollowClick(parseInt(id), type);
    } else {
      // Unsubscribe logic here
      onFollowClick(parseInt(id), type);
    }
  };

  /**
   * Renders the author name based on the specified nameType.
   * It can be a button (opens modal), a link (is a link), or a div (is not clickable / has no hover effect).
   * @returns JSX.Element representing the rendered author name.
   */
  const renderAuthorName = () => {
    const linkClass = layout === 'in-modal' ? 'link-20' : 'link-16';

    switch (nameType) {
      case 'button':
        return (
          <button className="author__name author__name--button" onClick={openAuthorModal}>
            <span className={linkClass}>{name}</span>
          </button>
        );
      case 'link':
        return (
          <a href={link} className="author__name author__name--link" rel="noopener noreferrer">
            <span className={linkClass}>{name || title}</span>
          </a>
        );
      case 'div':
        return (
          <div className="author__name author__name--no-hover">
            <span className={linkClass}>{name}</span>
          </div>
        );
      default:
        return (
          <div className="author__name author__name--no-hover">
            <span className={linkClass}>{name}</span>
          </div>
        );
    }
  };

  /**
   * Fetches and sets the topic vector if the type is "topic" and imageUrl is provided through wp-admin.
   * @returns {void}
   */
  useEffect(() => {
    if (type === 'topic' && imageUrl) {
      const fetchSvg = async () => {
        try {
          const response = await fetch(imageUrl);
          if (!response.ok) {
            throw new Error('Error fetching topic vector');
          }
          const text = await response.text();
          setTopicVector(text);
        } catch (error) {
          console.error('Error fetching the SVG:', error);
        }
      };

      fetchSvg();
    }
  }, [imageUrl, type]);

  /**
   * Renders the image based on the type prop.
   * If the type is "topic", it renders a div with the topicVector as inner HTML.
   * If the type is not "topic", it renders a div with an img element.
   *
   * @returns The rendered image element.
   */
  const renderImg = () => {
    let imageElement;

    if (type === 'topic') {
      imageElement = imageUrl ? (
        <div
          className={`author__image author__image--topic ${isFollowed ? 'subscribed' : ''}`}
          dangerouslySetInnerHTML={{ __html: topicVector }}
        />
      ) : (
        <div className={`author__image author__image--topic ${isFollowed ? 'subscribed' : ''}`}>
          <Icon name={'topic-hashtag'} size={'small'} />
        </div>
      );
    } else {
      imageElement = imageUrl ? (
        <div className={`author__image`}>
          <img src={imageUrl} alt={label || title} className="author__profile-image" />
        </div>
      ) : (
        <Icon name={'default-avatar'} size={'large'} />
      );
    }

    switch (nameType) {
      case 'button':
        return (
          <button className="author__image-button" onClick={openAuthorModal}>
            {imageElement}
          </button>
        );
      case 'link':
        return (
          <a href={link} className="author__image-link" rel="noopener noreferrer">
            {imageElement}
          </a>
        );
      case 'div':
      default:
        return imageElement;
    }
  };

  /**
   * Renders the social links based on the provided props.
   * Only render the containers if any of the social links are provided.
   *
   * @returns The JSX element containing the social links.
   */
  const renderSocialLinks = () => {
    if (hasSocialLinks && (email || twitter || linkedin || instagram)) {
      return (
        <div className="author__social-links">
          {email && (
            <a href={`mailto:${email}`} className="author__social-link link-underline-14" rel="noopener noreferrer">
              {email}
            </a>
          )}
          {twitter && (
            <a
              href={twitter}
              className="author__social-link link-underline-14"
              rel="noopener noreferrer"
              target="_blank"
            >
              {'X (Twitter)'}
            </a>
          )}
          {instagram && (
            <a
              href={instagram}
              className="author__social-link link-underline-14"
              rel="noopener noreferrer"
              target="_blank"
            >
              Instagram
            </a>
          )}
          {linkedin && (
            <a
              href={linkedin}
              className="author__social-link link-underline-14"
              rel="noopener noreferrer"
              target="_blank"
            >
              Linkedin
            </a>
          )}
        </div>
      );
    }
  };

  const renderAuthorDetails = () => {
    // Render the social links and subscribe button as variables so we can check if they have content
    const socialLinks = renderSocialLinks();
    const subscribeButton = isFollowable && layout !== 'full-width' && (
      <SubscribeButton
        type={type}
        onSubscribe={() => onFollowClick(parseInt(id), type)}
        isSubscribed={isFollowed}
        buttonType="button"
      />
    );

    // Check if any of the elements have content
    if (
      email ||
      twitter ||
      linkedin ||
      instagram ||
      shortDescription ||
      description ||
      socialLinks ||
      subscribeButton
    ) {
      return (
        <div className="author__details">
          {description && <p className="author__bio basic-16" dangerouslySetInnerHTML={{ __html: description }} />}
          {socialLinks}
          {subscribeButton}
        </div>
      );
    }

    // If none of the elements have content, return null
    return null;
  };

  // card for the carousels on the newsletter page
  const renderCarouselCard = (type: 'author' | 'topic') => {
    const cardClassName = `newsletter-author-topic-card newsletter-author-topic-card--${type}`;

    return (
      <div className={cardClassName}>
        {/* Mobile Layout */}
        <div className="newsletter-author-topic-card--mobile-layout">
          <div className="newsletter-author-topic-card__header">
            <div className="newsletter-author-topic-card__image-wrapper">
              <div className="newsletter-author-topic-card__image">{renderImg()}</div>
            </div>
            <div>
              <h5 className="newsletter-author-topic-card__title">{title}</h5>

              {type === 'author' && (
                <span
                  className="newsletter-author-topic-card__label basic-14"
                  dangerouslySetInnerHTML={{ __html: shortDescription || '' }}
                />
              )}
            </div>
          </div>
          <div className="newsletter-author-topic-card__content">
            <p
              className="newsletter-author-topic-card__description basic-14"
              dangerouslySetInnerHTML={{ __html: description || '' }}
            />

            <div className="newsletter-author-topic-card__bottom">
              <div className="newsletter-author-topic-card__subscribe-container">
                <label className="link-16">
                  <Toggle
                    checked={isFollowed}
                    icons={false}
                    onChange={handleToggleChange}
                    aria-label={
                      isFollowed
                        ? translations?.authorAndTopic?.unfollow || 'Unfollow'
                        : translations?.authorAndTopic?.follow || 'Follow'
                    }
                  />
                  {isFollowed ? (
                    <span>{translations?.authorAndTopic?.unfollow || 'Unfollow'}</span>
                  ) : (
                    <span>{translations?.authorAndTopic?.follow || 'Follow'}</span>
                  )}
                </label>
              </div>

              <a
                href={'newsletter.preview_url'}
                className="newsletter-author-topic-card__link link-16"
                target="_blank"
                rel="noreferrer"
              >
                {'View'}
                <Icon name="external-link" size="small" />
              </a>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="newsletter-author-topic-card--desktop-layout">
          <div className="newsletter-author-topic-card__image-wrapper">
            <div className="newsletter-author-topic-card__image">{renderImg()}</div>
          </div>

          <div className="newsletter-author-topic-card__content">
            <div>
              <div className="newsletter-author-topic-card__title-wrapper">
                <h5 className="newsletter-author-topic-card__title">{title}</h5>
                {type === 'author' && (
                  <span
                    className="newsletter-author-topic-card__label basic-14"
                    dangerouslySetInnerHTML={{ __html: shortDescription || '' }}
                  />
                )}
              </div>

              <p
                className="newsletter-author-topic-card__description basic-14"
                dangerouslySetInnerHTML={{ __html: description || '' }}
              />
            </div>

            <div className="newsletter-author-topic-card__bottom">
              <div className={`newsletter-author-topic-card__subscribe-container`}>
                <label className="link-16">
                  <Toggle
                    checked={isFollowed}
                    icons={false}
                    onChange={handleToggleChange}
                    aria-label={
                      isFollowed
                        ? translations?.authorAndTopic?.unfollow || 'Unfollow'
                        : translations?.authorAndTopic?.follow || 'Follow'
                    }
                  />
                  {isFollowed ? (
                    <span>{translations?.authorAndTopic?.unfollow || 'Unfollow'}</span>
                  ) : (
                    <span>{translations?.authorAndTopic?.follow || 'Follow'}</span>
                  )}
                </label>
              </div>

              <a href={'newsletter.preview_url'} className="newsletter-author-topic-card__link link-16">
                {'View'}
                <Icon name="external-link" size="small" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (layout === 'carousel') {
    return renderCarouselCard(type);
  }

  return (
    <>
      {layout === 'article-term' ? (
        <div className="term">
          <a href={link} className="term__name">
            <img className="term__image" src={imageUrl} alt={name || title} />
            <h6 className="link-16">{name || title}</h6>
          </a>

          <SubscribeButton
            type={type}
            onSubscribe={() => onFollowClick(parseInt(id), type)}
            isSubscribed={isFollowed}
            buttonType={'link'}
          />
        </div>
      ) : (
        <div className={layout === 'full-width' ? 'author-wrapper' : ''}>
          <div className={`author author--forbes author--${layout}`} data-name={title} data-code={id}>
            <div className="author__basics">
              {renderImg()}

              <div>
                {renderAuthorName()}

                {shortDescription && (
                  <p className="author__position basic-14" dangerouslySetInnerHTML={{ __html: shortDescription }} />
                )}

                {isFollowable && layout !== 'full-width' && (
                  <SubscribeButton
                    type={type}
                    onSubscribe={() => onFollowClick(parseInt(id), type)}
                    isSubscribed={isFollowed}
                    buttonType="link"
                    extraClassMod="author__follow-link--margin-top"
                  />
                )}
              </div>
            </div>
            {renderAuthorDetails()}
          </div>

          {/* Sub button for the new, full-width cards (with space-between) in MFA */}
          {isFollowable && layout === 'full-width' && (
            <SubscribeButton
              type={type}
              onSubscribe={() => onFollowClick(parseInt(id), type)}
              isSubscribed={isFollowed}
              buttonType="link"
            />
          )}
        </div>
      )}
    </>
  );
};

export default AuthorAndTopicCard;
